import { Button, Callout, FormGroup, InputGroup, Intent, Switch } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import React from 'react';
import { styled } from '@moleculeone/ui';

import { apiClient } from '@f/api-client';
import { saveAsCSV, saveAsJson } from '@f/utils/export';

import cls from './BatchSearchRequestExport.module.css';

const LIMIT = 10_000;

function getCurrentDay() {
  const date = new Date();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${date.getFullYear()}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getDateWithHours(dateString: string, [hours, minutes, seconds]: [number, number, number]) {
  const date = new Date(dateString);
  date.setUTCHours(hours, minutes, seconds);
  return date;
}

const ButtonsContainer = styled('div', {
  display: 'flex',
  gap: '0.5rem',
});

const ErrorCallout = styled(Callout, {
  marginBottom: '0.5rem',
});

export function BatchSearchRequestExport() {
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState(getCurrentDay());
  const [limit, setLimit] = React.useState(LIMIT);
  const [includeResults, setIncludeResults] = React.useState(true);
  const [isExportingCsv, setIsExportingCsv] = React.useState(false);
  const [isExportingJson, setIsExportingJson] = React.useState(false);
  const [exportingError, setExportingError] = React.useState('');

  const exportRequests = async (format: 'csv' | 'json') => {
    switch (format) {
      case 'csv':
        setIsExportingCsv(true);
        break;
      case 'json':
        setIsExportingJson(true);
        break;
      default:
        return;
    }
    setExportingError('');

    try {
      const createdAtQuery: {
        lte: Date;
        gte?: Date;
      } = {
        lte: getDateWithHours(dateTo, [23, 59, 59]),
      };
      if (dateFrom !== '') {
        createdAtQuery.gte = getDateWithHours(dateFrom, [0, 0, 0]);
      }

      const apiCalls = {
        csv: apiClient.search.adminExportSearchesBasicInfo,
        json: apiClient.search.adminExportSearches,
      };

      const { status, body } = await apiCalls[format]({
        query: {
          where: {
            createdAt: createdAtQuery,
          },
          orderBy: [
            {
              createdAt: 'desc',
            },
          ],
          skip: 0,
          take: limit,
          ...(format === 'json' && { includeResults }),
        },
      });

      if (status !== 200) {
        setExportingError('Fetching data to export failed');
        return;
      }

      if (body.length === 0) {
        setExportingError('No data to export within this date range');
        return;
      }

      const fileName = `batch-searches${dateFrom ? `_${dateFrom}` : ''}_${dateTo}`;

      switch (format) {
        case 'json':
          saveAsJson(body, `${fileName}.json`);
          break;
        case 'csv':
          saveAsCSV(body, `${fileName}.csv`, {
            useKeysAsHeaders: true,
          });
          break;
        default:
          setExportingError('Unsupported export format');
      }
    } catch (error) {
      setExportingError(error instanceof Error ? error.message : String(error));
    } finally {
      switch (format) {
        case 'csv':
          setIsExportingCsv(false);
          break;
        case 'json':
          setIsExportingJson(false);
          break;
        default:
          break;
      }
    }
  };

  const exportRequestsJson = () => {
    exportRequests('json');
  };

  const exportRequestsCsv = () => {
    exportRequests('csv');
  };

  return (
    <Popover2
      onClose={() => {
        setExportingError('');
      }}
      content={
        <form className={cls.popoverContent}>
          <div className={cls.dateRange}>
            <FormGroup label="From" labelFor="date-from">
              <InputGroup
                type="date"
                id="date-from"
                value={dateFrom}
                onChange={event => {
                  setDateFrom(event.target.value);
                }}
                max={dateTo}
              />
            </FormGroup>
            <FormGroup label="To" labelFor="date-to">
              <InputGroup
                type="date"
                id="date-to"
                value={dateTo}
                onChange={event => {
                  setDateTo(event.target.value);
                }}
                min={dateFrom}
                max={getCurrentDay()}
                required
              />
            </FormGroup>
          </div>
          <FormGroup label="Limit" labelFor="limit">
            <InputGroup
              type="number"
              id="limit"
              min={1}
              max={LIMIT}
              step={1}
              value={String(limit)}
              onChange={event => {
                setLimit(Number(event.target.value));
              }}
            />
          </FormGroup>
          <Switch
            labelElement="Include result info"
            id="includeResults"
            checked={includeResults}
            onChange={event => {
              setIncludeResults(event.currentTarget.checked);
            }}
          />
          {exportingError && <ErrorCallout intent={Intent.DANGER}>{exportingError}</ErrorCallout>}
          <ButtonsContainer>
            <Button
              text="Export CSV (basic info)"
              loading={isExportingCsv}
              intent={Intent.PRIMARY}
              large
              onClick={exportRequestsCsv}
            />
            <Button
              text="Export JSON (detailed info)"
              loading={isExportingJson}
              intent={Intent.PRIMARY}
              large
              onClick={exportRequestsJson}
            />
          </ButtonsContainer>
        </form>
      }
    >
      <Button text="Export" intent={Intent.PRIMARY} large />
    </Popover2>
  );
}
